import React from 'react';

// AbstractScene — vibrant procedural SVG patterns per occasion.
//
// Four pattern primitives (waves / blobs / arcs / stripes) compose every
// scene; each occasion is just a palette + preset parameters chosen for
// that mood. The viewBox is 350×200; cards render this via slice so the
// SVG fills however wide the card ends up.

const Grain = ({ id, strength = 0.18, seed = 4 }) => (
  <filter id={id} x="0%" y="0%" width="100%" height="100%">
    <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed={seed} stitchTiles="stitch" />
    <feColorMatrix values={`0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 ${strength} 0`} />
  </filter>
);

// Horizontal layered sine bands across the card.
const Waves = ({ palette, seed = 0, amplitude = 18, frequency = 1.6, layers = 5 }) => {
  const W = 350, H = 200, steps = 60;
  const path = (baseY, amp, freq, phase) => {
    const pts = [];
    for (let i = 0; i <= steps; i++) {
      const x = (i / steps) * W;
      const y = baseY + Math.sin((i / steps) * Math.PI * 2 * freq + phase) * amp;
      pts.push(`${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`);
    }
    pts.push(`L ${W} ${H} L 0 ${H} Z`);
    return pts.join(' ');
  };
  const out = [];
  for (let i = 0; i < layers; i++) {
    const t = i / Math.max(1, layers - 1);
    const baseY = 40 + t * (H - 80);
    const amp = amplitude * (1 - t * 0.35);
    const freq = frequency * (1 + t * 0.2);
    const phase = (i + seed) * 0.7;
    out.push(
      <path key={i} d={path(baseY, amp, freq, phase)}
            fill={palette[i % palette.length]} opacity={0.95 - i * 0.04} />
    );
  }
  return <>{out}</>;
};

// Overlapping organic blobs. Positions are hand-picked per seed for
// stable, on-brand composition.
const Blobs = ({ palette, blobs = 4, seed = 0 }) => {
  const sets = [
    [{ x: 80, y: 70, rx: 110, ry: 90, r: -8 },
     { x: 240, y: 130, rx: 130, ry: 90, r: 12 },
     { x: 170, y: 50, rx: 80, ry: 60, r: 28 },
     { x: 300, y: 40, rx: 70, ry: 60, r: -22 }],
    [{ x: 60, y: 140, rx: 130, ry: 100, r: 14 },
     { x: 250, y: 60, rx: 120, ry: 90, r: -10 },
     { x: 200, y: 160, rx: 90, ry: 70, r: 24 },
     { x: 320, y: 150, rx: 80, ry: 70, r: -16 }],
    [{ x: 110, y: 100, rx: 140, ry: 110, r: 6 },
     { x: 280, y: 90, rx: 110, ry: 90, r: -14 },
     { x: 60, y: 30, rx: 90, ry: 70, r: 20 },
     { x: 220, y: 180, rx: 100, ry: 70, r: -8 }],
    [{ x: 130, y: 60, rx: 120, ry: 100, r: 16 },
     { x: 260, y: 140, rx: 130, ry: 100, r: -12 },
     { x: 30, y: 160, rx: 90, ry: 70, r: 22 },
     { x: 330, y: 40, rx: 80, ry: 70, r: -20 }],
  ];
  const positions = sets[seed % sets.length];
  return (
    <g style={{ mixBlendMode: 'multiply' }}>
      {positions.slice(0, blobs).map((p, i) => (
        <ellipse key={i} cx={p.x} cy={p.y} rx={p.rx} ry={p.ry}
                 transform={`rotate(${p.r} ${p.x} ${p.y})`}
                 fill={palette[i % palette.length]} opacity="0.92" />
      ))}
    </g>
  );
};

// Concentric rings radiating from a corner.
const Arcs = ({ palette, origin = 'bottom-right', rings = 6 }) => {
  const ox = origin.includes('right') ? 350 : 0;
  const oy = origin.includes('bottom') ? 200 : 0;
  const out = [];
  for (let i = 0; i < rings; i++) {
    const r = 80 + i * 50;
    out.push(
      <circle key={i} cx={ox} cy={oy} r={r}
              fill="none" stroke={palette[i % palette.length]}
              strokeWidth={i % 2 === 0 ? 18 : 12}
              opacity={0.92 - i * 0.08} />
    );
  }
  return <>{out}</>;
};

// Diagonal coloured bands tiled across the card.
const Stripes = ({ palette, angle = 30, width = 38 }) => {
  const diag = 450;
  const count = Math.ceil(diag / width) + 2;
  return (
    <g transform={`rotate(${angle} 175 100)`}>
      <rect x="-150" y="-150" width="650" height="500" fill={palette[0]} />
      {Array.from({ length: count }).map((_, i) => (
        <rect key={i} x={-150 + i * width * 2} y="-150"
              width={width} height="500"
              fill={palette[(i + 1) % palette.length]} />
      ))}
    </g>
  );
};

// One preset per occasion — palette + pattern type tuned for the mood.
const PRESETS = {
  birthday:    { type: 'waves',   palette: ['#FF7A8A', '#FFA678', '#FFD37A', '#FFE4B5', '#FFB3C1'], amplitude: 22, frequency: 1.6, layers: 5, seed: 1 },
  mothersday:  { type: 'blobs',   palette: ['#E7A7D7', '#C599E0', '#F4C0D9', '#FFCFA8'], blobs: 4, seed: 0 },
  thankyou:    { type: 'waves',   palette: ['#9DCB8A', '#F4D78A', '#D9E6A6', '#7FB590', '#FFE9A3'], amplitude: 16, frequency: 1.4, layers: 5, seed: 2 },
  christmas:   { type: 'stripes', palette: ['#0F5C3F', '#D1493A', '#F2D27A', '#0F5C3F', '#C7E0CC'], angle: 35, width: 22 },
  valentines:  { type: 'blobs',   palette: ['#FF5577', '#FFA5B5', '#E83A6B', '#FFD0DA'], blobs: 4, seed: 1 },
  newyear:     { type: 'stripes', palette: ['#161A40', '#E8B845', '#27306A', '#F4D78A', '#161A40'], angle: -28, width: 26 },
  anniversary: { type: 'arcs',    palette: ['#5C1E2E', '#C9A24A', '#7A2840', '#E0BB6E', '#5C1E2E', '#F2D78A'], origin: 'bottom-right', rings: 6 },
  wedding:     { type: 'waves',   palette: ['#F1E3D3', '#E8C0C8', '#CDD9B9', '#F8EBD8', '#D9B9C6'], amplitude: 14, frequency: 1.2, layers: 5, seed: 3 },
  justbecause: { type: 'waves',   palette: ['#FF6B6B', '#FFB347', '#FFD93D', '#6BCB77', '#4D96FF', '#9D6BFF'], amplitude: 20, frequency: 1.8, layers: 6, seed: 0 },
  halloween:   { type: 'blobs',   palette: ['#3A1C4B', '#EB6E1F', '#7A3DA8', '#FFB347'], blobs: 4, seed: 2 },
  blossom:     { type: 'waves',   palette: ['#F4C9D5', '#C9DDB0', '#FFD7DD', '#9DCB8A', '#F8E6EA'], amplitude: 16, frequency: 1.5, layers: 5, seed: 4 },
  autumn:      { type: 'arcs',    palette: ['#B6452B', '#D78A3A', '#E8C56F', '#A55020', '#F3D89F', '#7C2D1A'], origin: 'top-left', rings: 6 },
  signature:   { type: 'waves',   palette: ['#29282D', '#007EA0', '#5A7DB8', '#9CC5DB', '#1C1C20'], amplitude: 18, frequency: 1.5, layers: 5, seed: 5 },
};

const AbstractScene = ({ occasion = 'signature' }) => {
  const preset = PRESETS[occasion] || PRESETS.signature;
  const grainId = `v2-abs-grain-${occasion}`;
  return (
    <svg className={`v2-abs v2-abs-${occasion}`} viewBox="0 0 350 200"
         preserveAspectRatio="xMidYMid slice" aria-hidden>
      <defs>
        <Grain id={grainId} strength={0.14} seed={3} />
      </defs>
      <rect width="350" height="200" fill={preset.palette[0]} />
      {preset.type === 'waves'   && <Waves   {...preset} />}
      {preset.type === 'blobs'   && <Blobs   {...preset} />}
      {preset.type === 'arcs'    && <Arcs    {...preset} />}
      {preset.type === 'stripes' && <Stripes {...preset} />}
      <rect width="350" height="200" filter={`url(#${grainId})`} opacity="0.45" />
    </svg>
  );
};

export default AbstractScene;
